import { addHours, addMinutes } from "date-fns";
import TableCell from "../components/TableEdit/TableCell";
import MultipleRowsCell from "../components/TableEdit/MultipleRowsCell";
import { createColumnHelper } from "@tanstack/react-table";

const computeAddOpTypeString = (operation, treatments, analysis, getLabel) => {
  if (operation?.treatments?.length > 1) {
    return `${getLabel(`operationType${operation.type}`)} (#${
      operation?.treatments?.length
    } ${getLabel("protocolTreatments")})`;
  }
  const treatment = operation?.treatments?.[0] || {};
  const protocol_type = treatment?.protocol_type || null;
  const subtype =
    operation?.type === "ADD"
      ? null
      : protocol_type
      ? getLabel(`drugLabelGroup_${protocol_type}_One`)?.toLowerCase?.()
      : null;

  return `${getLabel(
    `operationType${
      operation?.type === "ADD" ? treatment?.operation : operation.type
    }`
  )} ${subtype ? `(${subtype})` : ""}`;
};

const computeAdditionalTypeString = (
  operation,
  treatments,
  analysis,
  getLabel
) => {
  if (
    [
      "CUT",
      "DECANT",
      "RACKING",
      "ANALYSIS",
      "TREATMENT",
      "DESTEMMING",
      "NEW_LOT",
      "BOTTLING",
      "STABILIZATION_STATIC",
      "STABILIZATION_DYNAMIC",
      "STABILIZATION_RESIN",
    ].includes(operation?.type)
  )
    return "";
  const protocolType = operation?.treatments?.[0]?.protocol_type || null;

  if (
    operation?.completion_date === null &&
    [
      "STABILIZATION_STATIC",
      "STABILIZATION_DYNAMIC",
      "STABILIZATION_RESIN",
    ].includes(operation?.type)
  ) {
  }
  if (
    operation?.type === "OXYGENATION" &&
    ["MICRO", "MACRO", "CUSTOM"].includes(protocolType)
  )
    return ` (${getLabel(
      `protocolOXYGENATION_${protocolType}`
    )})`?.toLowerCase();
  if (
    operation?.type === "HOMOGENIZATION_MASS" &&
    ["TEMP", "REASSEMBLY"].includes(protocolType)
  )
    return ` (${getLabel(
      `operationTypeHOMOGENIZATION_${protocolType}`
    )})`?.toLowerCase();
  // TO DO: aggiungi cappello o altro in omogenizzazione temperatura

  return "";
};

const computeRecurrentString = (operation, treatments, analysis, getLabel) => {
  if (!["ANALYSIS", "TREATMENT"].includes(operation?.type)) return "";
  const arr = (operation?.type === "TREATMENT" ? treatments : analysis) || [];
  const protocolName =
    arr?.find((p) => p?.protocols_id === operation?.protocol_id)
      ?.protocols_name || "";
  const pName = protocolName?.length > 0 ? ` [${protocolName}]` : "";

  const customName = operation?.custom_name?.trim() || null;
  if (!operation?.repetitive) {
    if (customName) return ` #${customName}${pName}`;
    else return ` #${operation?.id}${pName}`;
  }

  const current = operation?.relative_id || 0;
  const total = operation?.total || 0;

  return current > 0 && total > 0
    ? ` #${
        customName || operation?.main_id || operation?.id
      } (${current}/${total})${pName}`
    : "";
};

const computeOperationRow = (row, treatments, analysis, getLabel) => {
  return row?.type === "ADD"
    ? computeAddOpTypeString(row, treatments, analysis, getLabel)
    : getLabel(`operationType${row?.type}`) +
        computeAdditionalTypeString(row, treatments, analysis, getLabel) +
        computeRecurrentString(row, treatments, analysis, getLabel);
};

/**
 * Compute total operation duration and add it to the start date.
 *
 * @param {Object} operation - The operation Object
 * @returns {Date} - The resulting end date
 */
const computeDynamicStabilizationEndDate = (operation) => {
  const startDate = operation?.start_date;

  if (!startDate) return null;

  const treatmentData = operation?.treatments?.[0] || {};

  // Ensure numeric values (default to 0)
  const iterations = Number(treatmentData?.iter_per_day) || 0;
  const pause = Number(treatmentData?.pause_time) || 0;
  const work = Number(treatmentData?.work_time) || 0;

  // Total time in hours and minutes
  const totalPauseHours = iterations * pause;
  const totalWorkMinutes = iterations * work;

  // Add to start date
  let endDate = new Date(startDate);
  endDate = addHours(endDate, totalPauseHours);
  endDate = addMinutes(endDate, totalWorkMinutes);

  return endDate;
};

const getStartDate = (start, isOperationStarted = false) => {
  const startDate = isOperationStarted ? start : new Date();
  return startDate;
};

const computeElapsedTime = (startDate, endDate, isOperationCompleted) => {
  let diffMs = endDate - startDate;
  const isNegative = diffMs < 0;
  diffMs = Math.abs(diffMs);

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const formatted = `${isOperationCompleted ? "" : isNegative ? "-" : "+"}${
    hours > 0 ? hours : ""
  }${hours > 0 ? "h:" : ""}${String(minutes).padStart(
    hours > 0 ? 2 : 1,
    "0"
  )}m`;

  return { hours, minutes, formatted, isNegative };
};

/**
 * Compute elapsed time between two dates (can be negative).
 * Returns both formatted string and a CSS class based on thresholds.
 *
 * @param {Date | string | number} start - Start date or timestamp
 * @param {Date | string | number} end - End date or timestamp
 * @param {Boolean} isOperationStarted - False when the op has to be started -> start_date = 1970
 * @param {Boolean} isOperationCompleted - False when the op has to be completed
 * @returns {{ formatted: string, className: string }}
 */
const getElapsedTimeWithClass = (
  start,
  end,
  isOperationStarted = false,
  isOperationCompleted = false
) => {
  const startDate = getStartDate(start, isOperationStarted);
  const endDate = new Date(end);

  const { hours, minutes, formatted, isNegative } = computeElapsedTime(
    startDate,
    endDate,
    isOperationCompleted
  );

  const WARNING_HOURS = 3;

  // --- Determine class ---
  let className = "";
  if (!isOperationCompleted) {
    // compute signed hours for threshold checks
    const signedHours = (endDate - startDate) / (1000 * 60 * 60);

    if (signedHours > 0) className = "expired";
    else if (signedHours >= -WARNING_HOURS) className = "warning";
  }

  // --- Determine custom formatted output ---
  let customFormatted;
  if (Math.abs(hours) >= 24) {
    // Convert to days + remaining hours
    const totalHours = Math.abs(hours);
    const days = Math.floor(totalHours / 24);
    const remainingHours = Math.floor(totalHours % 24);

    // Format: "Xd:Yh"
    customFormatted = `${days}d:${remainingHours}h`;
    if (isNegative) customFormatted = `-${customFormatted}`;
  } else {
    // Use original formatted output under 24h
    customFormatted = formatted;
  }

  return { formatted: customFormatted, className, hours, minutes };
};

const computeFlowRates = (liters, hours, minutes) => {
  const totalMinutes = hours * 60 + minutes;
  const totalHours = hours + minutes / 60;

  return {
    l_per_min: liters / totalMinutes,
    l_per_hour: liters / totalHours,
  };
};

const flattenDrugIngredients = (concs) => {
  if (!Array.isArray(concs)) return [];
  return concs.flatMap((c) => {
    if (!Array.isArray(c.ingredients) || c.ingredients.length === 0) return [];

    return c.ingredients.map((ingredient) => ({
      drug_id: c.drug_id,
      product_name: c.drug_name,
      ingredient_name: ingredient.substance,
      quantity: (c.quantity * ingredient.quantity) / 100,
      unit: c.unit,
      type: ingredient.type || null,
      functional: ingredient.functional || null,
      physical: ingredient.physical || null,
      origins: ingredient.origins || [],
    }));
  });
};

const normalizeString = (str) => str?.trim()?.toLowerCase(); // Normalize names

const aggregateDrugConcentrations = (
  isDrugConcentration,
  isAdjuvant,
  compositions = []
) => {
  if (!Array.isArray(compositions)) return [];

  // 1️⃣ Filtro per tipo
  const filtered = compositions.filter((item) => {
    if (!isAdjuvant) return item.type === "ADDITIVE";
    return true;
  });

  // 2️⃣ Aggrega
  const aggregated = Object.values(
    filtered.reduce((acc, item) => {
      // Decidi la chiave di aggregazione
      const key = isDrugConcentration
        ? normalizeString(item.ingredient_name) // ingredienti separati
        : normalizeString(item.product_name);   // somma per prodotto

      if (!acc[key]) {
        acc[key] = { ...item, quantity: item?.quantity || 0 };
      } else {
        acc[key].quantity += item.quantity;
      }

      return acc;
    }, {})
  );

  return aggregated;
};


const computeRequiredNumericQty = (substance, totalVolumeLiters = 0) => {
  const unit_id = substance?.unit_id;
  const quantity = substance?.quantity;

  if (!unit_id || !quantity || !totalVolumeLiters) return 0;

  switch (unit_id) {
    case 1: // mg/L → kg
      return (totalVolumeLiters * quantity) / 1_000_000;

    case 2: // g/hL → kg
      return ((totalVolumeLiters / 100) * quantity) / 1000;

    case 6: // g/L → kg
      return (totalVolumeLiters * quantity) / 1000;

    case 7: // g/100Kg ≈ g/100L → kg
      return ((totalVolumeLiters / 100) * quantity) / 1000;

    case 4: // Kg
      return quantity;

    case 3: // L (liquid additives ≈ kg)
      return quantity;

    case 5: // Unit
      return quantity;

    default:
      return 0;
  }
};

const computeRequiredWarehouseQty = (warehouseUnit, requiredNumericQty = 0) => {
  if (!warehouseUnit) return 0;

  switch (warehouseUnit.value) {
    case "kg":
      return requiredNumericQty; // ✅ already kg

    case "l":
      return requiredNumericQty; // liters already

    case "unit":
      return requiredNumericQty;

    default:
      return 0;
  }
};

const getWarehouseUnit = (substance, getLabel) => {
  const unit_id = Number(substance?.unit_id);

  switch (unit_id) {
    case 1:
    case 2:
    case 4:
    case 6:
    case 7:
      return { value: "kg", label: getLabel("drugBatchUnit_Kg") };
    case 3:
      return { value: "l", label: getLabel("drugBatchUnit_L") };
    case 5:
      return { value: "unit", label: getLabel("drugBatchUnit_Unit") };
    default:
      return null;
  }
};

const groupBySubstanceId = (items = []) => {
  const map = new Map();
  
  for (const item of items) {
    const { substance_id, unit, unit_id, drug_batches = [] } = item;

    let warehouse_unit = null;
    
    if (!map.has(substance_id)) {
      warehouse_unit = [1, 2, 6, 7].includes(unit_id)
        ? "Kg"
        : unit_id === 3
        ? "L"
        : unit;

      map.set(substance_id, {
        substance_id,
        drug_batches: [],
      });
    }

    const isEnoughStock = true;
    map.get(substance_id).drug_batches.push(
      ...drug_batches?.map((b) => {
        return {
          ...b,
          substance_id,
          is_enough_stock: isEnoughStock,
          label: b?.batch_number,
          value: b?.batch_number,
          unit: warehouse_unit,
          warehouse_unit: warehouse_unit,
        };
      })
    );
  }

  return Array.from(map.values());
};

const buildBatchField = (substance_id, batch, quantity, warehouseUnit) => ({
  value: batch.batch_number,
  label: batch.batch_number,
  batch_number: batch.batch_number,
  quantity,
  substance_id: substance_id,
  expire_date: batch.expire_date,
  warehouse_unit: warehouseUnit?.label,
});

const assignDrugBatches = (
  substances,
  totalTreatmentVolume,
  automaticDrugBatches,
  getLabel,
  isExcludeBatchNumber = false,
  isModal = true
) => {
  const assigments = [];
  const substancesWithBatches =
    substances?.filter(
      (b) => Array.isArray(b?.batches) && b?.batches?.length > 0
    ) || [];

  for (let i = 0; i < substancesWithBatches?.length; i++) {
    const drug = substancesWithBatches?.[i];
    const drugId = drug?.substance_id || drug?.id;
    const filteredBatches =
      (drug?.batches || [])?.filter((b) =>
        isExcludeBatchNumber ? b?.batch_number !== isExcludeBatchNumber : true
      ) || [];
    const sortedBatches = filteredBatches
      .sort((a, b) => new Date(a.expire_date) - new Date(b.expire_date))
      ?.map((b) => {
        return { ...b, value: b?.batch_number, label: b?.batch_number };
      });

    const warehouseUnit = getWarehouseUnit(drug, getLabel);
    // const requiredNumericQty = computeRequiredWarehouseQty(warehouseUnit, requiredNumericQty);
    const requiredNumericQty = computeRequiredNumericQty(
      drug,
      totalTreatmentVolume
    );
    let remaining = computeRequiredWarehouseQty(
      warehouseUnit,
      requiredNumericQty
    );

    const allocation = sortedBatches
      .map((batch) => {
        if (remaining <= 0) return null;

        const used = Math.min(batch.stock, remaining);
        remaining -= used;

        return buildBatchField(drugId, batch, used, warehouseUnit);
      })
      .filter(Boolean);

    if (allocation.length) {
      if (remaining <= 0) allocation[0].is_enough_stock = true;
      if (!isModal) automaticDrugBatches.replace(allocation); // update only the modal
      assigments[drugId] = allocation;
    }
  }
  if (assigments.length && isModal) {
    automaticDrugBatches.replace(assigments); // update the whole table entries
  }
};

// Helper: add or update a treatment row
const upsertTreatment = (tmp_treatments_data, tmp) => {
  const index = tmp_treatments_data.findIndex(
    (t) => t.substance_id === tmp.substance_id
  );
  if (index > -1) tmp_treatments_data[index] = tmp;
  else tmp_treatments_data.push(tmp);
};

const buildTreatmentsDataArray = (
  allSubstances = [],
  drug_batches = [],
  data,
  protocols,
  protocol_id,
  type,
  id,
  operation,
  operations,
  analysisType
) => {
  const treatments_data = [];
  const tmp_treatments_data = [];

  const removedSubIds = [];
  const addedSubIds = [];

  const getSubstanceById = (id) =>
    allSubstances?.find((s) => s?.id === Number(id));

  const isTrue = (val) => val?.value === true || val === true;
  const isFalse = (val) => val?.value === false || val === false;

  // Initialize removed and added substances
  if (typeof data?.removed === "object") {
    for (const [substance_id, isRemoved] of Object.entries(data.removed)) {
      const id = Number(substance_id);
      if (isTrue(isRemoved)) removedSubIds.push(id);
      else if (isFalse(isRemoved)) addedSubIds.push(id);
    }
  }

  const protocolToUse = protocols?.find(
    (p) =>
      p?.protocols_id === (id ? operation?.protocol_id : protocol_id?.value)
  );
  const protocolSubstances =
    protocolToUse?.[type === "TREATMENT" ? "drugs" : "analysis"] || [];

  // CREATE
  if (!id) {
    for (const s of protocolSubstances) {
      const substance = getSubstanceById(s.substance_id);
      if (!substance) continue;

      const tmp = {
        substance_id: substance.id,
        quantity: Number(s.quantity),
        unit_id: substance.unit_id,
        removed: removedSubIds.includes(substance.id),
      };
      if (!tmp.removed && addedSubIds.includes(substance.id)) tmp.added = true;

      upsertTreatment(tmp_treatments_data, tmp);
    }
  } else {
    // UPDATE
    const existingOp = operations?.find((o) => o.id === Number(id));

    for (const t of existingOp?.treatments || []) {
      if (!addedSubIds.includes(t.substance_id))
        addedSubIds.push(t.substance_id);
      if (
        !tmp_treatments_data.some((tt) => tt.substance_id === t.substance_id)
      ) {
        const tmp = { ...t, removed: removedSubIds.includes(t.substance_id) };
        delete tmp.tank_id;
        delete tmp.batch_id;
        upsertTreatment(tmp_treatments_data, tmp);
      }
    }
  }
  
  // Handle updated columns
  if (typeof data?.column === "object") {
    for (const [key, new_value] of Object.entries(data.column)) {
      if (!new_value?.substance_id) continue;

      const substance = getSubstanceById(new_value?.id || key);
      if (!substance) continue;

      const val =
        Number(new_value?.new) >= 0
          ? Number(new_value?.new)
          : substance.quantity || 0;
      const substance_id = substance?.substance_id || substance?.id;

      const existing = tmp_treatments_data.find(
        (t) => t.substance_id === substance_id
      );

      const tmp = {
        ...(existing || {}),
        substance_id,
        quantity: val,
        unit_id: substance.unit_id,
        unit: substance.unit,
        max_range: substance.max_range,
        min_range: substance.min_range,
        protocol_id: data?.protocol_id?.value,
        substance_name: substance.name,
        info: {
          analysisType: analysisType?.value,
          protocolFrequency: data?.protocolFrequency?.value,
        },
        ...(addedSubIds.includes(substance.id) && { added: true }),
        ...(removedSubIds.includes(substance.id) && { removed: true }),
      };

      if (existing) {
        upsertTreatment(tmp_treatments_data, tmp);
      } else if (
        substance_id &&
        (type === "ANALYSIS" || (type === "TREATMENT" && val > 0))
      ) {
        upsertTreatment(tmp_treatments_data, tmp);
      }
    }
  }

  // Handle drug batches (TREATMENT only)
  if (type === "TREATMENT" && Array.isArray(drug_batches)) {
    for (const treatment of tmp_treatments_data) {
      const drugBatchesPerSubstance =
        drug_batches?.[treatment.substance_id] || [];

      // if (drugBatchesPerSubstance === null) {
      //   toast.error(
      //     getLabel("toast_drugBatchInvalid", { name: treatment?.substance_name }),
      //     {
      //       toastId: customIdDrugBatches,
      //       position: toast.POSITION.BOTTOM_RIGHT,
      //       exclude: true,
      //       autoClose: 5000,
      //     }
      //   );
      //   return;
      // }

      if (
        addedSubIds.includes(treatment.substance_id) ||
        !protocolToUse?.substance_ids?.includes(treatment.substance_id)
      ) {
        treatment.added = true;
      }

      if (drugBatchesPerSubstance) {
        treatment.drug_batches = drugBatchesPerSubstance || [];
      }
    }
  }

  // Final assignment
  if (type === "TREATMENT") {
    treatments_data.push(...tmp_treatments_data);
  } else {
    treatments_data.push(
      ...tmp_treatments_data
        .filter((t) => t?.added || t?.removed === false)
        .map((t) => ({
          substance_id: t.substance_id,
          added: t.added || !!id,
        }))
    );
  }
  return treatments_data;
};

const getUsedDrugBatches = (treatments = []) => {
  const map = new Map();

  for (const treatment of treatments) {
    for (const batch of treatment.drug_batches || []) {
      const id = batch.drug_batch_id;
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, {
          drug_batch_id: id,
          drug_batch_number: batch.batch_number,
          unitary_cost: batch.unitary_cost,
          quantity: 0,
        });
      }

      map.get(id).quantity += Number(batch.quantity) || 0;
    }
  }

  return Array.from(map.values());
};

const groupDrugBatchesBySubstance = (treatments = [], substance_id = null) => {
  const result = {};

  for (const treatment of (treatments || [])?.filter((t) =>
    substance_id ? t?.substance_id === substance_id : true
  )) {
    const substanceId = treatment?.substance_id;
    if (!substanceId) continue;

    if (!result[substanceId]) {
      result[substanceId] = [];
    }

    for (const batch of treatment.drug_batches || []) {
      result[substanceId].push({
        tank_id: batch.tank_id,
        quantity: Number(batch.quantity) || 0,
        drug_batch_number: batch.batch_number,
        unitary_cost: batch.unitary_cost,
        drug_batch_id: batch.drug_batch_id,
        warehouse_unit: [1, 2, 6, 7].includes(treatment?.unit_id)
          ? "Kg"
          : treatment?.unit_id === 3
          ? "L"
          : treatment?.unit,
      });
    }
  }

  return result;
};

const getUsedDrugBatchesBySubstance = (
  treatments = [],
  substance_id = null
) => {
  const result = new Map();

  for (const treatment of (treatments || [])?.filter((t) =>
    substance_id ? t?.substance_id === substance_id : true
  )) {
    const substanceId = treatment?.substance_id;
    if (!substanceId) continue;

    if (!result.has(substanceId)) {
      result.set(substanceId, new Map());
    }

    const batchMap = result.get(substanceId);

    for (const batch of treatment.drug_batches || []) {
      const batchId = batch?.drug_batch_id;
      if (!batchId) continue;

      if (!batchMap.has(batchId)) {
        batchMap.set(batchId, {
          drug_batch_id: batchId,
          batch_number: batch.batch_number,
          unitary_cost: batch.unitary_cost,
          quantity: 0,
        });
      }

      batchMap.get(batchId).quantity += Number(batch.quantity) || 0;
    }
  }

  // Convert Maps → plain objects
  return Object.fromEntries(
    Array.from(result.entries()).map(([substanceId, batchMap]) => [
      substanceId,
      Array.from(batchMap.values()),
    ])
  );
};

const getTreatmentColumns = (
  type,
  isShowCost,
  register,
  getLabel = () => null
) => {
  const columnHelper = createColumnHelper();

  const columns =
    type === "CLEANING"
      ? [
          columnHelper.accessor(
            (row) => row?.day + (row?.to_day ? ` - ${row?.to_day}` : ""),
            {
              header: getLabel("protocolStep"),
              cell: TableCell,
              meta: {
                type: "number",
                register: register,
              },
            }
          ),
          ...(type !== "CLEANING"
            ? [
                columnHelper.accessor(
                  (row) =>
                    row?.phase ? getLabel(`protocolPhase_${row.phase}`) : "",
                  {
                    header: getLabel("protocolPhase"),
                    cell: TableCell,
                  }
                ),
              ]
            : []),
          columnHelper.accessor(
            (row) =>
              row?.operation
                ? getLabel(`protocolOperation_${row.operation}`)
                : "",
            {
              header: getLabel("protocolOperation"),
              cell: TableCell,
              meta: {
                type: "text",
                register: register,
              },
            }
          ),
          columnHelper.accessor(
            (row) =>
              row.operation === "RINSE" || row.operation === "VAPORIZATION"
                ? getLabel("protocolProduct_H2O")
                : row?.product_name || "",
            {
              header: getLabel("protocolProduct"),
              cell: TableCell,
              meta: {
                type: "text",
                register: register,
              },
            }
          ),
          columnHelper.accessor(
            (row) =>
              row?.quantity
                ? row.operation === "RINSE" || row.operation === "VAPORIZATION"
                  ? `${row?.quantity}`
                  : row.operation === "CLEANING"
                  ? `${row?.quantity} %`
                  : ""
                : "",
            {
              header: getLabel("protocolConcentration"),
              cell: TableCell,
              meta: {
                type: "number",
                register: register,
                isFilterDisabled: true,
              },
            }
          ),
          columnHelper.accessor(
            (row) =>
              row?.operation === "HOMOGENIZATION_MASS"
                ? ""
                : row?.work_time
                ? `${row?.work_time} min`
                : "",
            {
              header: getLabel("protocolHomogenizationContactTime"),
              cell: TableCell,
              meta: {
                type: "number",
              },
            }
          ),
          // ...(treatments?.operations?.find(
          //   (o) => o?.operation === "HOMOGENIZATION_MASS"
          // )
          //   ? [
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.protocol_type
          //             ? getLabel(`protocolType_${row?.protocol_type}`) +
          //               (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
          //               row?.homogenization_type
          //                 ? ` (${getLabel(
          //                     `protocolHomogenizationMass_${row?.homogenization_type}`
          //                   )})`
          //                 : "") +
          //               (row?.protocol_type === "CUSTOM"
          //                 ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
          //                 : "")
          //             : "",
          //         {
          //           header: getLabel("protocolType"),
          //           cell: TableCell,
          //           meta: {
          //             type: "text",
          //           },
          //         }
          //       ),
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.work_time &&
          //           (row?.pause_time || row?.homogenization_pause_time)
          //             ? `${row?.work_time}min / ${
          //                 row?.pause_time || row?.homogenization_pause_time
          //               }h  ${
          //                 type === "CLEANING" &&
          //                 row.operation === "HOMOGENIZATION_MASS" &&
          //                 row?.iter_per_day
          //                   ? `x ${row?.iter_per_day}`
          //                   : ""
          //               }`
          //             : "",
          //         {
          //           header: getLabel("protocolWorkPauseTime"),
          //           cell: TableCell,
          //           meta: {
          //             type: "number",
          //           },
          //         }
          //       ),
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.homogenization_motor_speed
          //             ? row?.homogenization_motor_speed + "%"
          //             : "",
          //         {
          //           header: getLabel("protocolMotorSpeed"),
          //           cell: TableCell,
          //           meta: {
          //             type: "number",
          //           },
          //         }
          //       ),
          //     ]
          //   : []),
        ]
      : [
          columnHelper.accessor("substance_name", {
            header: getLabel("treatmentName"),
            cell: TableCell,
            meta: {
              isFilterDisabled: true,
            },
          }),
          columnHelper.accessor(
            (row) => `${row?.quantity} ${row?.unit || ""}`,
            {
              header: getLabel("quantity"),
              cell: TableCell,
              meta: {
                isFilterDisabled: true,
              },
            }
          ),
          // columnHelper.accessor("unit", {
          //   header: getLabel("unit"),
          //   cell: TableCell,
          // }),
          columnHelper.accessor(
            (row) =>
              row?.type ? getLabel(`drugType_${row.type?.toUpperCase()}`) : "",
            {
              header: getLabel("drugType"),
              cell: TableCell,
              meta: {
                type: "text",
                isFilterDisabled: true,
                filterVariant: "text",
              },
            }
          ),
          // columnHelper.accessor(
          //   (row) =>
          //     parseInt(row?.min_range) >= 0 && parseInt(row?.max_range) >= 0
          //       ? `${String(row?.min_range) || "0"} - ${
          //           String(row?.max_range) || ""
          //         } ${row?.unit}`
          //       : "",
          //   {
          //     header: getLabel("substanceRange"),
          //     cell: TableCell,
          //     meta: {
          //       type: "number",
          //       isFilterDisabled: true,
          //     },
          //   }
          // ),
          columnHelper.accessor(
            (row) =>
              `${
                row?.min_temperature ? String(row?.min_temperature) : "n/a"
              } - ${
                row?.max_temperature ? String(row?.max_temperature) : "n/a"
              } \u00b0C` || "",
            {
              header: getLabel("substanceTemperature"),
              cell: TableCell,
              meta: {
                type: "number",
                isFilterDisabled: true,
              },
            }
          ),
          columnHelper.accessor("drug_batches", {
            header: getLabel("drugBatchNumber"),
            cell: MultipleRowsCell,
            meta: {
              array: "drug_batches",
              selector: "drug_batch_number",
              isFilterDisabled: true,
            },
          }),
        ];

  if (isShowCost) {
    columns.push(
      columnHelper.accessor(
        (row) =>
          `${
            (row?.drug_batches || [])?.length > 0
              ? (row?.drug_batches || [])
                  ?.reduce((acc, curr) => {
                    return (
                      acc +
                        Number(curr?.unitary_cost) *
                          Number(curr?.quantity) *
                          ([1, 2, 3, 6, 7].includes(row.unit_id) ? 1000 : 1) ||
                      0
                    );
                  }, 0)
                  .toFixed(2) || 0
              : "-"
          } ${(row?.drug_batches || [])?.length > 0 ? "€" : ""}`,
        {
          header: getLabel("drugBatchProductCost"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        }
      )
    );
  }
  return columns;
};

export {
  assignDrugBatches,
  groupBySubstanceId,
  getWarehouseUnit,
  computeRequiredNumericQty,
  computeRequiredWarehouseQty,
  aggregateDrugConcentrations,
  flattenDrugIngredients,
  computeDynamicStabilizationEndDate,
  computeOperationRow,
  getElapsedTimeWithClass,
  getStartDate,
  computeElapsedTime,
  computeFlowRates,
  buildTreatmentsDataArray,
  getUsedDrugBatchesBySubstance,
  groupDrugBatchesBySubstance,
  getUsedDrugBatches,
  getTreatmentColumns,
};
